import React, { useState, useEffect, useCallback } from 'react';
import { MaybeLayout } from '../components/layout/MaybeLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  CheckCircle2, AlertTriangle, Clock, RefreshCw, User as UserIcon,
  CheckSquare, Trash2,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';
import { adminAPI } from '../lib/api';

const fmtTime = (iso) => {
  if (!iso) return '–';
  try {
    return format(parseISO(iso), 'dd.MM.yyyy HH:mm', { locale: de });
  } catch {
    return iso;
  }
};

export default function TimeTrackingHealth({ embedded = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingAction, setPendingAction] = useState(null); // { entry, action, employeeName }
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminAPI.timeTrackingOrphans();
      setData(r.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runAction = async () => {
    if (!pendingAction) return;
    setBusy(true);
    try {
      const { entry, action } = pendingAction;
      if (action === 'close') {
        await adminAPI.closeOrphanEntry(entry.id);
        toast.success('Eintrag um 21:00 geschlossen (ArbZG-Ruhezeit)');
      } else {
        await adminAPI.deleteOrphanEntry(entry.id);
        toast.success('Eintrag gelöscht');
      }
      setPendingAction(null);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Aktion fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <MaybeLayout embedded={embedded}>
        <div className="space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-32" />
        </div>
      </MaybeLayout>
    );
  }

  const total = data?.total_entries || 0;
  const problematic = data?.problematic_count || 0;
  const employees = data?.employees || [];

  return (
    <MaybeLayout embedded={embedded}>
      <div className="space-y-6" data-testid="time-tracking-health-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
              <Clock className="h-6 w-6 text-primary" />
              Zeiterfassung Health-Check
            </h1>
            <p className="text-muted-foreground mt-1">
              Findet offene Stempel und vor allem <strong>mehrfache</strong> offene Einträge
              pro Mitarbeiter — z.B. nach einem Deploy oder vergessenen Ausstempeln.
            </p>
          </div>
          <Button variant="outline" onClick={load} data-testid="refresh-tt-health-btn">
            <RefreshCw className="h-4 w-4 mr-2" />
            Neu prüfen
          </Button>
        </div>

        {problematic === 0 ? (
          <Alert className="border-green-500/50 bg-green-50 dark:bg-green-900/10" data-testid="tt-health-ok">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertTitle>Alles in Ordnung</AlertTitle>
            <AlertDescription>
              {total === 0
                ? 'Aktuell gibt es keine offenen Stempel.'
                : `${total} offene Stempel insgesamt, kein Mitarbeiter mit Mehrfacheinträgen.`}
            </AlertDescription>
          </Alert>
        ) : (
          <Alert className="border-red-500/50 bg-red-50 dark:bg-red-900/10" data-testid="tt-health-problem">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <AlertTitle>
              {problematic} Mitarbeiter mit Doppeleinträgen
            </AlertTitle>
            <AlertDescription>
              Diese Mitarbeiter haben mehr als einen offenen Stempel — bitte prüfen und
              die übrigen Einträge per Korrektur abschließen oder löschen.
            </AlertDescription>
          </Alert>
        )}

        {employees.length > 0 && (
          <Card data-testid="tt-health-list">
            <CardHeader>
              <CardTitle className="flex items-center justify-between text-base">
                <span>Offene Stempel ({total})</span>
                <Badge variant={problematic > 0 ? 'destructive' : 'secondary'}>
                  {problematic > 0
                    ? `${problematic} kritisch`
                    : `${employees.length} Mitarbeiter`}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {employees.map((emp) => (
                <div
                  key={emp.employee_id}
                  className={`rounded-lg border p-4 ${
                    emp.is_problematic
                      ? 'border-red-300 bg-red-50/50 dark:bg-red-900/10'
                      : 'border-border'
                  }`}
                  data-testid={`tt-health-emp-${emp.employee_id}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <UserIcon className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{emp.employee_name}</span>
                    <Badge variant={emp.is_problematic ? 'destructive' : 'outline'}>
                      {emp.open_count} offen
                    </Badge>
                  </div>
                  <ul className="space-y-2 ml-6">
                    {emp.entries.map((e) => (
                      <li
                        key={e.id}
                        className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm"
                        data-testid={`tt-health-entry-${e.id}`}
                      >
                        <span className="font-mono text-xs text-muted-foreground">{e.date}</span>
                        <span className="text-muted-foreground">·</span>
                        <span className="text-muted-foreground">Eingestempelt: {fmtTime(e.clock_in)}</span>
                        <span className="text-muted-foreground">·</span>
                        <span className="text-muted-foreground">Status: {e.status}</span>
                        <div className="ml-auto flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              setPendingAction({ entry: e, action: 'close', employeeName: emp.employee_name })
                            }
                            data-testid={`close-entry-${e.id}`}
                          >
                            <CheckSquare className="h-3.5 w-3.5 mr-1" />
                            Schließen 21:00
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:text-destructive"
                            onClick={() =>
                              setPendingAction({ entry: e, action: 'delete', employeeName: emp.employee_name })
                            }
                            data-testid={`delete-entry-${e.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5 mr-1" />
                            Löschen
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        <AlertDialog
          open={!!pendingAction}
          onOpenChange={(open) => !open && !busy && setPendingAction(null)}
        >
          <AlertDialogContent data-testid="orphan-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>
                {pendingAction?.action === 'close'
                  ? 'Eintrag um 21:00 schließen?'
                  : 'Eintrag wirklich löschen?'}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {pendingAction?.action === 'close' ? (
                  <>
                    Der offene Stempel von <strong>{pendingAction?.employeeName}</strong> vom{' '}
                    <span className="font-mono">{pendingAction?.entry.date}</span> wird automatisch
                    um <strong>21:00 Uhr (Berlin)</strong> ausgestempelt — das schützt die
                    gesetzliche ArbZG-Ruhezeit von 11 Stunden. ArbZG-Pausen werden korrekt
                    abgezogen und der Mitarbeiter erhält eine Benachrichtigung.
                    <br />
                    <span className="text-xs">
                      (Falls der Einstempel-Zeitpunkt nach 21:00 lag, wird automatisch 23:59 als Schließzeit verwendet.)
                    </span>
                  </>
                ) : (
                  <>
                    Der offene Stempel von <strong>{pendingAction?.employeeName}</strong> vom{' '}
                    <span className="font-mono">{pendingAction?.entry.date}</span> wird komplett
                    gelöscht. <strong>Diese Aktion kann nicht rückgängig gemacht werden.</strong>{' '}
                    Nutzen Sie diese Option nur für eindeutig falsche Doppeleinträge.
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={busy}>Abbrechen</AlertDialogCancel>
              <AlertDialogAction
                onClick={runAction}
                disabled={busy}
                className={
                  pendingAction?.action === 'delete'
                    ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                    : ''
                }
                data-testid="orphan-confirm-action-btn"
              >
                {busy
                  ? 'Bitte warten…'
                  : pendingAction?.action === 'close'
                  ? 'Schließen'
                  : 'Löschen'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </MaybeLayout>
  );
}
